import type {ThreadUserMessagePart} from '@assistant-ui/core';
import {
  AuiIf,
  ComposerPrimitive,
  useAui,
  useAuiState,
} from '@assistant-ui/react';
import {
  Button,
  DropdownMenu,
  IconButton,
  type IconName,
  TagGroup,
} from '@wandb/components';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  createEditor,
  Editor,
  Element as SlateElement,
  Text as SlateText,
  Transforms,
} from 'slate';
import {withHistory} from 'slate-history';
import {ReactEditor, withReact} from 'slate-react';

import {toAUIContentParts} from '../../agentChat/messageConversions';
import {DEFAULT_PROJECT_NAME} from '../../agentChat/runtime/backend';
import {
  hasMeaningfulContent,
  useActiveThreadPendingQuestions,
} from '../../agentChat/store';
import {useDebounceState} from '../../util/hooks';
import {useNavContext} from '../NavContextProvider';
import {imagePartToDataUrl, toImagePart} from './attachments/imageParts';
import {ImageThumbnail} from './attachments/ImageThumbnail';
import {COMPOSER_INITIAL_VALUE} from './constants';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {useTrackWBAgentComposer} from './useTrackWBAgent';
import {buildPromptPartsFromSlate} from './utils';
import {WBAgentAgentSelector} from './WBAgentAgentSelector';
import {useWBAgentContextStore} from './WBAgentContextStore';
import {WBAgentDebugViewToggle} from './WBAgentDebugView';
import {WBAgentEditor} from './WBAgentEditor';
import {WBAgentQuestionCarousel} from './WBAgentQuestionCarousel';
import {
  useRunMentionSearch,
  WBAgentRunMentionDropdown,
} from './WBAgentRunMentionDropdown';

const ComposerAction = ({
  isEmpty,
  onSend,
  onStop,
}: {
  isEmpty: boolean;
  onSend: () => void;
  onStop: () => void;
}) => {
  return (
    <div className="flex">
      <AuiIf condition={s => !s.thread.isRunning}>
        <IconButton
          isDisabled={isEmpty}
          aria-label="send message"
          variant="primary"
          icon="up"
          onPress={onSend}
        />
      </AuiIf>
      <AuiIf condition={s => s.thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <IconButton
            aria-label="cancel query"
            icon="agent-stop"
            onPress={onStop}
          />
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </div>
  );
};

const CUSTOM_VOID_TYPES = new Set(['run-mention']);

export const WBAgentComposer = ({
  onStartAddImage,
}: {
  onStartAddImage: () => void;
}) => {
  const aui = useAui();
  const isRunning = useAuiState(s => s.thread.isRunning);
  const {params} = useNavContext();
  const {entityName, projectName} = params;
  const nightModeRevertFilter = useNightModeRevertFilter();

  const pendingQuestions = useActiveThreadPendingQuestions();
  // Escape hatch: lets the user dismiss the question carousel and type a normal
  // message instead. Reset whenever a new set of questions arrives.
  const [typeInstead, setTypeInstead] = useState(false);
  useEffect(() => {
    if (pendingQuestions) {
      setTypeInstead(false);
    }
  }, [pendingQuestions]);

  // Per-turn agent config override from the (internal-only) picker. Sent with
  // the next message and otherwise ignored; resets when the composer remounts.
  const [agentConfigOverride, setAgentConfigOverride] = useState<string | null>(
    null
  );

  const [isComposerEmpty, setIsComposerEmpty] = useState(true);
  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [_liveQuery, debouncedMentionQuery, setMentionQuery] = useDebounceState(
    '',
    300
  );

  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const mentionActive = isMentionOpen && !!entityName && !!projectName;
  const mentionRuns = useRunMentionSearch(
    entityName ?? '',
    projectName ?? '',
    mentionActive ? debouncedMentionQuery : ''
  );
  const clampedHighlightedIndex =
    mentionRuns.length === 0
      ? 0
      : Math.min(highlightedIndex, mentionRuns.length - 1);

  const selectedRuns = useWBAgentContextStore(s => s.selectedRuns);
  const removeRun = useWBAgentContextStore(s => s.removeRun);
  const syncProject = useWBAgentContextStore(s => s.syncProject);
  const composerDraft = useWBAgentContextStore(s => s.composerDraft);
  const setComposerDraft = useWBAgentContextStore(s => s.setComposerDraft);
  const composerImages = useWBAgentContextStore(s => s.composerImages);
  const removeComposerImage = useWBAgentContextStore(
    s => s.removeComposerImage
  );
  const failedComposerImages = useWBAgentContextStore(
    s => s.failedComposerImages
  );
  const removeFailedComposerImage = useWBAgentContextStore(
    s => s.removeFailedComposerImage
  );
  const clearComposerImages = useWBAgentContextStore(
    s => s.clearComposerImages
  );
  const {
    trackMentionAdded,
    trackMentionRemoved,
    trackMessageSent,
    trackTurnStopped,
  } = useTrackWBAgentComposer();

  const editor = useMemo(() => {
    const e = withHistory(withReact(createEditor()));
    const {isInline, isVoid} = e;
    e.isInline = element => {
      return element.type != null && CUSTOM_VOID_TYPES.has(element.type)
        ? true
        : isInline(element);
    };
    e.isVoid = element => {
      return element.type != null && CUSTOM_VOID_TYPES.has(element.type)
        ? true
        : isVoid(element);
    };
    return e;
  }, []);

  // Clear runs context if user changes projects
  useEffect(() => {
    syncProject(projectName);
  }, [projectName, syncProject]);

  const getAtMatch = (editor: Editor) => {
    const {selection} = editor;
    if (!selection) {
      return null;
    }
    const [node] = Editor.node(editor, selection.anchor);
    if (!SlateText.isText(node)) {
      return null;
    }
    const textBeforeCursor = node.text.slice(0, selection.anchor.offset);
    return textBeforeCursor.match(/@([\w-]*)$/);
  };

  const handleSlateChange = useCallback(() => {
    const voidNodeEntries = [
      ...Editor.nodes(editor, {
        at: [],
        match: n =>
          SlateElement.isElement(n) &&
          n.type != null &&
          CUSTOM_VOID_TYPES.has(n.type),
      }),
    ];
    setIsComposerEmpty(
      Editor.string(editor, []).trim().length === 0 &&
        voidNodeEntries.length === 0
    );
    setComposerDraft(editor.children);
    const atMatch = getAtMatch(editor);
    // Run mentions require an entity/project to query against, so suppress
    // the dropdown off-project rather than intercept Enter for an empty list.
    if (atMatch && entityName && projectName) {
      setIsMentionOpen(true);
      setMentionQuery(atMatch[1]);
      setHighlightedIndex(0);
    } else {
      setIsMentionOpen(false);
      setMentionQuery('');
    }
  }, [editor, entityName, projectName, setMentionQuery, setComposerDraft]);

  const handleRunSelect = (displayName: string, name: string) => {
    if (!entityName || !projectName) {
      return;
    }

    const atMatch = getAtMatch(editor);
    if (atMatch) {
      Transforms.delete(editor, {
        distance: atMatch[0].length,
        unit: 'character',
        reverse: true,
      });
    }
    Transforms.insertNodes(editor, {
      type: 'run-mention',
      displayName,
      name,
      entityName,
      projectName,
      children: [{text: ''}],
    });
    Transforms.move(editor);
    Transforms.insertText(editor, ' ');

    setIsMentionOpen(false);
    setMentionQuery('');
    ReactEditor.focus(editor);
    trackMentionAdded('composer_dropdown', 'run');
  };

  const clearComposer = () => {
    editor.children = COMPOSER_INITIAL_VALUE;
    Transforms.deselect(editor);
    editor.onChange();
  };

  const handleSend = () => {
    if (isRunning) {
      return;
    }

    const userParts = buildPromptPartsFromSlate(editor.children, selectedRuns, {
      entityName: entityName ?? '',
      projectName: projectName ?? '',
    });

    // Add images after the user's text. `getVisiblePromptParts` slices
    // everything before the first text part as ambient context, so images must
    // follow a text part to survive the AUI roundtrip.
    // For an image-only send, `buildPromptPartsFromSlate` drops its empty-text
    // delimiter, so reintroduce one to anchor the images.
    if (composerImages.length > 0) {
      if (!userParts.some(p => p.type === 'text')) {
        userParts.push({type: 'text', text: '', status: 'complete'});
      }
      for (const image of composerImages) {
        userParts.push(toImagePart(image));
      }
    }

    if (!hasMeaningfulContent(userParts)) {
      return;
    }

    aui.thread().append({
      role: 'user',
      content: userParts.flatMap(toAUIContentParts) as ThreadUserMessagePart[],
      runConfig: {custom: {agentConfigOverride}},
    });
    trackMessageSent('composer');
    clearComposer();
    clearComposerImages();
  };

  // Submit the carousel's assembled answers as a plain-text next prompt. The
  // run-started event clears `pendingQuestions`, reverting to the editor.
  const submitAnswers = (prompt: string) => {
    if (isRunning) {
      return;
    }
    aui.thread().append({
      role: 'user',
      content: [{type: 'text', text: prompt}] as ThreadUserMessagePart[],
      runConfig: {custom: {agentConfigOverride}},
    });
    trackMessageSent('composer');
  };

  const handleComposerKeyDown = (e: React.KeyboardEvent) => {
    // Ignore IME composition events
    if (e.nativeEvent.isComposing) {
      return;
    }

    if (e.key === 'Backspace') {
      const inlineVoid = Editor.above(editor, {
        match: n =>
          SlateElement.isElement(n) &&
          Editor.isVoid(editor, n) &&
          Editor.isInline(editor, n),
      });
      if (inlineVoid) {
        e.preventDefault();
        Editor.deleteBackward(editor);
        return;
      }
    }
    if (isMentionOpen) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setIsMentionOpen(false);
        setMentionQuery('');
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (mentionRuns.length > 0) {
          setHighlightedIndex(i => (i + 1) % mentionRuns.length);
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (mentionRuns.length > 0) {
          setHighlightedIndex(i => (i > 0 ? i - 1 : mentionRuns.length - 1));
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const run = mentionRuns[clampedHighlightedIndex];
        if (run?.displayName) {
          handleRunSelect(run.displayName, run.name);
        }
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const tagItems = [
    ...(params.projectName && params.projectName !== DEFAULT_PROJECT_NAME
      ? [
          {
            id: 'project name',
            icon: 'folder-project' as IconName,
            textValue: params.projectName,
            removable: false,
            cssTruncate: true,
          },
        ]
      : []),
    ...selectedRuns.map(run => ({
      id: run.name,
      icon: 'running-repeat' as IconName,
      textValue: run.displayName,
      cssTruncate: true,
    })),
  ];

  if (pendingQuestions && pendingQuestions.length > 0 && !typeInstead) {
    return (
      <div className="relative">
        <WBAgentQuestionCarousel
          questions={pendingQuestions}
          isDisabled={isRunning}
          onSubmit={submitAnswers}
          onTypeInstead={() => setTypeInstead(true)}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {pendingQuestions && pendingQuestions.length > 0 && typeInstead && (
        <div className="mb-8 flex">
          <Button variant="ghost" onPress={() => setTypeInstead(false)}>
            Show questions
          </Button>
        </div>
      )}
      <ComposerPrimitive.Root className="flex flex-col gap-8 rounded border border-moon-250 bg-white p-12 focus-within:border-[var(--focus-ring-color)] focus-within:shadow-[0_0_0_3px_#CDF4F7]">
        {tagItems.length > 0 && (
          <div
            className="[&_[role=row]]:max-w-[300px]"
            style={{filter: nightModeRevertFilter}}>
            <TagGroup
              aria-label="composer context tags"
              items={tagItems}
              onRemove={key => {
                removeRun(String(key));
                trackMentionRemoved('run');
              }}
            />
          </div>
        )}
        {(composerImages.length > 0 || failedComposerImages.length > 0) && (
          <div className="flex flex-start gap-8 pt-4 self-stretch overflow-x-auto [scrollbar-width:thin]">
            {failedComposerImages.map(image => (
              <ImageThumbnail
                key={image.id}
                src={image.previewUrl}
                error="Upload failed"
                onRemove={() => removeFailedComposerImage(image.id)}
              />
            ))}
            {composerImages.map(image => (
              <ImageThumbnail
                key={image.id}
                src={imagePartToDataUrl(image)}
                onRemove={() => removeComposerImage(image.id)}
              />
            ))}
          </div>
        )}
        <WBAgentEditor
          editor={editor}
          initialValue={composerDraft}
          onChange={handleSlateChange}
          onKeyDown={handleComposerKeyDown}
        />
        {mentionActive && (
          <WBAgentRunMentionDropdown
            runs={mentionRuns}
            highlightedIndex={clampedHighlightedIndex}
            onHover={setHighlightedIndex}
            onSelect={handleRunSelect}
          />
        )}
        <div className="flex w-full items-center gap-4">
          <DropdownMenu.Trigger>
            <IconButton
              icon="add-new"
              variant="ghost"
              aria-label="Add content"
            />
            <DropdownMenu.Content placement="top start">
              <DropdownMenu.Item onAction={onStartAddImage}>
                <DropdownMenu.ItemDecorationLeft
                  iconProps={{icon: 'photo-picture'}}
                />
                <DropdownMenu.ItemLabel>Add images</DropdownMenu.ItemLabel>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Trigger>
          <WBAgentDebugViewToggle />
          <WBAgentAgentSelector
            value={agentConfigOverride}
            onChange={setAgentConfigOverride}
          />
          <div className="ml-auto">
            <ComposerAction
              isEmpty={
                isComposerEmpty &&
                selectedRuns.length === 0 &&
                composerImages.length === 0
              }
              onSend={handleSend}
              onStop={trackTurnStopped}
            />
          </div>
        </div>
      </ComposerPrimitive.Root>
    </div>
  );
};
